import { useEffect, useMemo, useRef, useState } from "react";
import { CaretDown, CaretUp, Printer, DownloadSimple, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";

const BAND = {
  continuous: { label: "Compliant all the way through", cls: "bg-[#27AE60]/15 text-[#1e8449]" },
  lapsed_before: { label: "Lapsed before — compliant now", cls: "bg-[#B7791F]/15 text-[#8a5d14]" },
  out_now: { label: "Out of compliance now", cls: "bg-[#C0392B]/15 text-[#C0392B]" },
};

export default function ComplianceTimeline() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({});
  const [openTopic, setOpenTopic] = useState({});
  const [printAll, setPrintAll] = useState(false);
  const [topicPick, setTopicPick] = useState(null); // null = all topics
  const [mandatoryIds, setMandatoryIds] = useState(null);
  const didDefaultPick = useRef(false);

  useEffect(() => {
    api.get("/training/compliance-timeline").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/training/topics").then((r) => setMandatoryIds(new Set((r.data || []).filter((t) => t.state_mandated).map((t) => t.id)))).catch(() => {});
    const before = () => setPrintAll(true);
    const after = () => setPrintAll(false);
    window.addEventListener("beforeprint", before);
    window.addEventListener("afterprint", after);
    return () => { window.removeEventListener("beforeprint", before); window.removeEventListener("afterprint", after); };
  }, []);

  const downloadCsv = async () => {
    try {
      const r = await api.get("/training/compliance-timeline", { params: { format: "csv" }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "training-compliance-timeline.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("CSV download failed"); }
  };

  if (!data) return <div className="flex justify-center py-10"><CircleNotch size={22} className="animate-spin text-primary" /></div>;
  const s = data.summary || {};
  const expanded = (id) => printAll || open[id];

  const allTopics = [];
  const seen = new Set();
  for (const p of data.people || []) {
    for (const t of p.topics || []) {
      if (!seen.has(t.topic_id)) { seen.add(t.topic_id); allTopics.push({ topic_id: t.topic_id, title: t.title }); }
    }
  }
  allTopics.sort((a, b) => a.title.localeCompare(b.title));

  if (!didDefaultPick.current && mandatoryIds && allTopics.length > 0) {
    const ids = new Set(allTopics.filter((t) => mandatoryIds.has(t.topic_id)).map((t) => t.topic_id));
    if (ids.size > 0) { didDefaultPick.current = true; setTopicPick(ids); }
  }

  const visibleTopics = (p) => (p.topics || []).filter((t) => !topicPick || topicPick.has(t.topic_id));
  const visiblePeople = (data.people || []).filter((p) => visibleTopics(p).length > 0);

  const printReport = () => {
    setPrintAll(true);
    setTimeout(() => window.print(), 200);
  };

  const printTargets = () => {
    const esc = (x) => String(x ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
    const today = new Date().toISOString().slice(0, 10);
    const scope = topicPick ? allTopics.filter((t) => topicPick.has(t.topic_id)).map((t) => t.title).join(", ") : "all annual trainings";
    const personHtml = visiblePeople.map((p) => {
      const rows = visibleTopics(p).map((t) => {
        const origDue = t.first_due || t.would_have_been_due || "—";
        const done = t.events.length
          ? t.events.map((e) => `${esc(e.date)}${e.days_late ? ` <span class="late">${e.days_late}d late</span>` : ""}`).join("<br>")
          : '<span class="bad">NO RECORD</span>';
        const nextDue = t.currently_compliant
          ? esc(t.current_through || "—")
          : t.events.length ? `<span class="bad">OVERDUE — ${t.days_out_now}d out</span>` : `<span class="bad">OVERDUE since ${esc(t.would_have_been_due || "?")}</span>`;
        return `<tr><td>${esc(t.title)}</td><td>${esc(origDue)}</td><td>${done}</td><td>${nextDue}</td></tr>`;
      }).join("");
      return `<h2>${esc(p.name)} <span class="meta">${esc(p.employee_id || "")}${p.start_date ? ` · hired ${esc(p.start_date)}` : ""} · ${BAND[p.band].label}</span></h2>
        <table><thead><tr><th>Training</th><th>Originally due</th><th>Completed (actual dates)</th><th>Compliant through / status</th></tr></thead><tbody>${rows}</tbody></table>`;
    }).join("");
    const w = window.open("", "_blank");
    w.document.write(`<!doctype html><html><head><title>Training date targets — ${today}</title><style>
      body{font-family:Georgia,serif;margin:32px;color:#1c1917;font-size:11px}
      h1{font-size:18px;margin:0} .sub{color:#78716c;font-size:11px;margin:4px 0 18px}
      h2{font-size:13px;margin:18px 0 4px;page-break-after:avoid}
      h2 .meta{font-weight:normal;color:#78716c;font-size:10px}
      table{width:100%;border-collapse:collapse;page-break-inside:auto}
      th{text-align:left;color:#78716c;text-transform:uppercase;font-size:8.5px;letter-spacing:.05em;padding:3px 8px;border-bottom:1.5px solid #6a3e97}
      td{padding:4px 8px;border-bottom:1px solid #e7e5e4;vertical-align:top}
      tr{page-break-inside:avoid} .late{color:#B7791F;font-weight:bold} .bad{color:#C0392B;font-weight:bold}
      @media print{body{margin:10mm}}
      </style></head><body>
      <h1>Silver Home Care — Training compliance date targets</h1>
      <p class="sub">Printed ${today} · ${visiblePeople.length} people · scope: ${esc(scope)} · "Originally due" = the date the training had to be done to stay compliant (hire/start + 365 days; then every 365 days from the last completion)</p>
      ${personHtml}
      </body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  };

  return (
    <div className="space-y-3" data-testid="compliance-timeline">
      <div className="flex flex-wrap items-center justify-between gap-2 print:hidden">
        <p className="text-xs text-stone-500">
          Per person, per annual training: every completion on the 365-day chain — what was due when, how late it was,
          and any window where compliance lapsed and when it came back. Use this to hunt down paper records.
        </p>
        <div className="flex gap-2">
          <Button size="sm" onClick={printTargets} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="timeline-print-targets">
            <Printer size={14} className="mr-1" /> Print date-targets list
          </Button>
          <Button size="sm" variant="outline" onClick={printReport} className="border-stone-300" data-testid="timeline-print">
            <Printer size={14} className="mr-1" /> Print report (expanded)
          </Button>
          <Button size="sm" variant="outline" onClick={downloadCsv} className="border-stone-300" data-testid="timeline-csv">
            <DownloadSimple size={14} className="mr-1" /> CSV
          </Button>
        </div>
      </div>

      <div className="print:hidden" data-testid="timeline-topic-picker">
        <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-stone-400">Your primary mandatory trainings are pre-selected — tap any training to show or hide it:</p>
        <div className="flex flex-wrap gap-1.5">
          {allTopics.map((t) => {
            const on = !topicPick || topicPick.has(t.topic_id);
            return (
              <button key={t.topic_id} type="button"
                onClick={() => setTopicPick((cur) => {
                  const next = new Set(cur || allTopics.map((x) => x.topic_id));
                  if (next.has(t.topic_id)) next.delete(t.topic_id); else next.add(t.topic_id);
                  return next.size === allTopics.length ? null : next;
                })}
                className={`rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors ${on ? "border-primary/50 bg-primary/10 text-primary" : "border-stone-200 text-stone-400"}`}
                data-testid={`timeline-topic-pick-${t.topic_id}`}>
                {t.title}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {[["continuous", s.continuous], ["lapsed_before", s.lapsed_before], ["out_now", s.out_now]].map(([k, n]) => (
          <div key={k} className="rounded-lg border border-stone-200 bg-white p-3 text-center shadow-sm" data-testid={`timeline-summary-${k}`}>
            <p className="font-heading text-2xl font-bold text-stone-900">{n || 0}</p>
            <p className="text-[11px] text-stone-500">{BAND[k].label}</p>
          </div>
        ))}
      </div>

      {visiblePeople.map((p) => (
        <div key={p.user_id} className="rounded-lg border border-stone-200 bg-white shadow-sm" data-testid={`timeline-person-${p.user_id}`}>
          <button type="button" onClick={() => setOpen((o) => ({ ...o, [p.user_id]: !o[p.user_id] }))}
            className="flex w-full flex-wrap items-center gap-2 px-4 py-3 text-left" data-testid={`timeline-person-toggle-${p.user_id}`}>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-stone-900">{p.name} <span className="font-normal text-stone-400">{p.employee_id && `· ${p.employee_id}`}{p.start_date && ` · hired ${p.start_date}`}</span></p>
              <p className="text-xs text-stone-400">{p.topics.length} annual training(s) tracked{p.topics_out_now > 0 && ` · ${p.topics_out_now} out now`}</p>
            </div>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${BAND[p.band].cls}`} data-testid={`timeline-band-${p.user_id}`}>{BAND[p.band].label}</span>
            <span className="print:hidden">{expanded(p.user_id) ? <CaretUp size={15} className="text-stone-400" /> : <CaretDown size={15} className="text-stone-400" />}</span>
          </button>
          {expanded(p.user_id) && (
            <div className="space-y-2 border-t border-stone-100 px-4 py-3">
              {visibleTopics(p).map((t) => {
                const key = `${p.user_id}:${t.topic_id}`;
                const tOpen = printAll || openTopic[key];
                const late = t.events.filter((e) => e.lapsed_before).length;
                return (
                  <div key={key} className="rounded-md border border-stone-100" data-testid={`timeline-topic-${key}`}>
                    <button type="button" onClick={() => setOpenTopic((o) => ({ ...o, [key]: !o[key] }))}
                      className="flex w-full items-center justify-between px-3 py-2 text-left">
                      <p className="text-xs font-medium text-stone-800">{t.title}</p>
                      <span className="flex items-center gap-2">
                        {t.events.length === 0 && <span className="rounded-full bg-[#C0392B]/15 px-2 py-0.5 text-[11px] font-semibold text-[#C0392B]">no record — was due {t.would_have_been_due}</span>}
                        {late > 0 && <span className="rounded-full bg-[#B7791F]/15 px-2 py-0.5 text-[11px] font-semibold text-[#8a5d14]">{late} late/lapsed</span>}
                        {t.events.length > 0 && (
                          <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${t.currently_compliant ? "bg-[#27AE60]/15 text-[#1e8449]" : "bg-[#C0392B]/15 text-[#C0392B]"}`}>
                            {t.currently_compliant ? `compliant through ${t.current_through}` : `out ${t.days_out_now}d`}
                          </span>
                        )}
                      </span>
                    </button>
                    {tOpen && t.events.length > 0 && (
                      <div className="border-t border-stone-100 px-3 py-2">
                        {t.first_due && <p className="text-[11px] text-stone-400">First completion was due by {t.first_due} (start + 365d)</p>}
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {t.events.map((e, i) => (
                            <span key={i} className={`rounded-md border px-2 py-1 text-[11px] ${e.lapsed_before ? "border-[#C0392B]/40 bg-[#C0392B]/5 text-[#C0392B]" : "border-[#27AE60]/30 bg-[#27AE60]/5 text-[#1e8449]"}`}
                              data-testid={`timeline-event-${key}-${i}`}>
                              {e.date}{e.due_by ? ` · due ${e.due_by}` : ""}{e.days_late ? ` · ${e.days_late}d late` : ""}
                            </span>
                          ))}
                        </div>
                        {(t.lapses || []).map((l, i) => (
                          <p key={i} className="mt-1.5 rounded bg-[#C0392B]/5 px-2 py-1 text-[11px] text-[#C0392B]" data-testid={`timeline-lapse-${key}-${i}`}>
                            Lapse: compliance lost {l.lost_on} → restored {l.restored_on} ({l.days_out} days out)
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
